/**
 * LogViewerModal - Componente generico per visualizzare log/cronologia
 *
 * Componente riusabile per visualizzare log di qualsiasi tipo.
 * Configurabile tramite props per adattarsi a diversi contesti:
 * - Workflow ordini magazzino
 * - Audit log utenti
 * - Log operazioni turbine
 * - Storico modifiche clienti
 * - etc.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, Search, Filter, RefreshCw, ChevronLeft, ChevronRight,
  Trash2, AlertTriangle, Loader2, Check
} from 'lucide-react';
import ResizableModal from '../ui/ResizableModal';

// ============================================================
// TYPES
// ============================================================

export interface LogColumn {
  id: string;
  label: string;
  width?: string;
  render: (log: LogEntry) => React.ReactNode;
}

export interface LogActionConfig {
  icon: React.ElementType;
  color: string;
  label: string;
}

export interface LogFilter {
  id: string;
  label: string;
  type: 'select' | 'date' | 'text';
  options?: { value: string; label: string }[];
  placeholder?: string;
}

export interface LogEntry {
  id: number | string;
  [key: string]: unknown;
}

export interface CleanupResult {
  logs_count?: number;
  ordini_count?: number;
  sample?: Array<{ [key: string]: string }>;
  deleted_count?: number;
  message?: string;
  executed?: boolean;
}

export interface LogViewerConfig {
  title: string;
  subtitle?: string;
  // API
  fetchEndpoint: string;
  cleanupEndpoint?: string;
  cleanupDays?: number;
  // Columns
  columns: LogColumn[];
  // Actions mapping (es. azione -> config)
  actionConfig?: Record<string, LogActionConfig>;
  defaultActionConfig?: LogActionConfig;
  // Filters
  filters?: LogFilter[];
  // Search
  searchFields?: string[];
  searchPlaceholder?: string;
  // Customization
  emptyMessage?: string;
  emptyIcon?: React.ElementType;
  // Callbacks
  onLogClick?: (log: LogEntry) => void;
}

interface LogViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: LogViewerConfig;
}

// ============================================================
// COMPONENT
// ============================================================

const LogViewerModal: React.FC<LogViewerModalProps> = ({ isOpen, onClose, config }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState<Record<string, string[]>>({});

  // Paginazione
  const [page, setPage] = useState(0);
  const [limit] = useState(50);

  // Filtri attivi
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Cleanup state
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);

  // Dimensioni modale: 80% della finestra
  const modalWidth = Math.min(Math.round(window.innerWidth * 0.8), 1400);
  const modalHeight = Math.min(Math.round(window.innerHeight * 0.8), 900);

  // ============================================================
  // API CALLS
  // ============================================================

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', (page * limit).toString());

      // Aggiungi filtri attivi
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`${config.fetchEndpoint}?${params.toString()}`);
      const data = await response.json();

      if (data.success) {
        setLogs(data.logs || data.data || []);
        setTotal(data.total || data.count || 0);
        if (data.filtri) setFilterOptions(data.filtri);
      } else {
        setError(data.error || 'Errore nel caricamento dei log');
      }
    } catch (err) {
      setError('Errore di connessione');
      console.error('[LogViewerModal] Errore:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, activeFilters, config.fetchEndpoint]);

  const handleCleanupDryRun = async () => {
    if (!config.cleanupEndpoint) return;
    setCleanupLoading(true);
    try {
      const days = config.cleanupDays || 7;
      const response = await fetch(`${config.cleanupEndpoint}?dry_run=true&days=${days}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setCleanupResult(data);
      } else {
        setError(data.error);
      }
    } catch {
      setError('Errore durante verifica cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupExecute = async () => {
    if (!config.cleanupEndpoint) return;
    setCleanupLoading(true);
    try {
      const days = config.cleanupDays || 7;
      const response = await fetch(`${config.cleanupEndpoint}?days=${days}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setCleanupResult({ ...data, executed: true });
        loadLogs();
        setTimeout(() => {
          setShowCleanupModal(false);
          setCleanupResult(null);
        }, 2000);
      } else {
        setError(data.error);
      }
    } catch {
      setError('Errore durante cleanup');
    } finally {
      setCleanupLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadLogs();
    }
  }, [isOpen, loadLogs]);

  // ============================================================
  // FILTERING
  // ============================================================

  const totalPages = Math.ceil(total / limit);

  // Filtra localmente per ricerca testo
  const filteredLogs = searchQuery && config.searchFields
    ? logs.filter(log =>
        config.searchFields!.some(field => {
          const value = log[field];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.toLowerCase());
        })
      )
    : logs;

  // ============================================================
  // RENDER HELPERS
  // ============================================================

  const EmptyIcon = config.emptyIcon || Clock;

  const handleFilterChange = (filterId: string, value: string) => {
    setActiveFilters(prev => ({ ...prev, [filterId]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery('');
    setPage(0);
  };

  const hasActiveFilters = Object.values(activeFilters).some(v => v) || searchQuery;

  // ============================================================
  // HEADER ACTIONS
  // ============================================================

  const headerActions = (
    <div className="flex items-center gap-2">
      {config.cleanupEndpoint && (
        <button
          onClick={() => {
            setShowCleanupModal(true);
            handleCleanupDryRun();
          }}
          className="flex items-center gap-1 px-2 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 rounded transition-colors text-xs"
        >
          <Trash2 className="w-3 h-3" />
          <span className="hidden sm:inline">Pulizia</span>
        </button>
      )}
      {config.filters && config.filters.length > 0 && (
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1 px-2 py-1 rounded transition-all text-xs ${
            showFilters ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          <Filter className="w-3 h-3" />
          <span className="hidden sm:inline">Filtri</span>
        </button>
      )}
      <button
        onClick={loadLogs}
        disabled={loading}
        className="flex items-center gap-1 px-2 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors text-xs"
      >
        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      <ResizableModal
        isOpen={isOpen}
        onClose={onClose}
        title={config.title}
        subtitle={config.subtitle}
        headerActions={headerActions}
        initialWidth={modalWidth}
        initialHeight={modalHeight}
        minWidth={500}
        minHeight={350}
        enableResize={true}
        enableDrag={true}
        forceCenter={true}
        footer={
          <div className="flex justify-end px-4 py-3 border-t border-slate-700">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Chiudi
            </button>
          </div>
        }
      >
        <div className="flex flex-col h-full p-3">
          {/* Filtri Panel */}
          <AnimatePresence>
            {showFilters && config.filters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 overflow-hidden flex-shrink-0"
              >
                <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Ricerca */}
                    {config.searchFields && (
                      <div className="lg:col-span-2">
                        <label className="block text-xs text-slate-400 mb-1">Cerca</label>
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={config.searchPlaceholder || 'Cerca...'}
                            className="w-full pl-8 pr-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}

                    {/* Dynamic filters */}
                    {config.filters.map(filter => (
                      <div key={filter.id}>
                        <label className="block text-xs text-slate-400 mb-1">{filter.label}</label>
                        {filter.type === 'select' ? (
                          <select
                            value={activeFilters[filter.id] || ''}
                            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">Tutti</option>
                            {(filter.options || filterOptions[filter.id] || []).map(opt => (
                              <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
                                {typeof opt === 'string' ? opt : opt.label}
                              </option>
                            ))}
                          </select>
                        ) : filter.type === 'date' ? (
                          <input
                            type="date"
                            value={activeFilters[filter.id] || ''}
                            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-xs focus:ring-2 focus:ring-indigo-500"
                          />
                        ) : (
                          <input
                            type="text"
                            value={activeFilters[filter.id] || ''}
                            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                            placeholder={filter.placeholder}
                            className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:ring-2 focus:ring-indigo-500"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Clear filters */}
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="mt-2 text-xs text-indigo-400 hover:text-indigo-300"
                    >
                      Rimuovi tutti i filtri
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stats */}
          <div className="mb-2 flex items-center justify-between flex-shrink-0">
            <div className="text-slate-400 text-xs">
              {total > 0 ? (
                <>Mostrando <span className="text-white font-medium">{filteredLogs.length}</span> di <span className="text-white font-medium">{total}</span> log</>
              ) : (
                'Nessun log trovato'
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-2 p-2 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-xs flex-shrink-0">
              {error}
            </div>
          )}

          {/* Table */}
          <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden flex-1 min-h-0 flex flex-col">
            <div className="overflow-auto flex-1">
              <table className="w-full">
                <thead className="bg-slate-700/50 sticky top-0">
                  <tr>
                    {config.columns.map(col => (
                      <th
                        key={col.id}
                        className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase"
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {loading ? (
                    <tr>
                      <td colSpan={config.columns.length} className="px-3 py-8 text-center">
                        <RefreshCw className="w-5 h-5 animate-spin mx-auto text-indigo-400" />
                        <p className="mt-2 text-slate-400 text-sm">Caricamento...</p>
                      </td>
                    </tr>
                  ) : filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan={config.columns.length} className="px-3 py-8 text-center text-slate-400">
                        <EmptyIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{config.emptyMessage || 'Nessun log trovato'}</p>
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => (
                      <tr
                        key={log.id}
                        className={`hover:bg-slate-700/30 transition-colors ${config.onLogClick ? 'cursor-pointer' : ''}`}
                        onClick={() => config.onLogClick?.(log)}
                      >
                        {config.columns.map(col => (
                          <td key={col.id} className="px-3 py-2">
                            {col.render(log)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-3 py-2 border-t border-slate-700 flex items-center justify-between">
                <div className="text-xs text-slate-400">
                  Pagina {page + 1} di {totalPages}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </ResizableModal>

      {/* Cleanup Modal */}
      <AnimatePresence>
        {showCleanupModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            onClick={() => !cleanupLoading && setShowCleanupModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md shadow-2xl"
            >
              <div className="p-4 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">Pulizia Log Vecchi</h3>
                    <p className="text-xs text-slate-400">Elimina log più vecchi di {config.cleanupDays || 7} giorni</p>
                  </div>
                </div>
              </div>

              <div className="p-4">
                {cleanupLoading && !cleanupResult ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                  </div>
                ) : cleanupResult ? (
                  <div className="space-y-3">
                    {cleanupResult.executed ? (
                      <div className="flex items-center gap-2 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                        <Check className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="text-green-400 font-medium text-sm">Cleanup completato!</p>
                          <p className="text-xs text-green-300/80">
                            Eliminati {cleanupResult.deleted_count} log
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 p-3 bg-amber-500/20 border border-amber-500/30 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-amber-400" />
                          <div>
                            <p className="text-amber-400 font-medium text-sm">
                              {cleanupResult.logs_count === 0 ? 'Nessun log da eliminare' : `${cleanupResult.logs_count} log da eliminare`}
                            </p>
                          </div>
                        </div>

                        {cleanupResult.sample && cleanupResult.sample.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs text-slate-400">Anteprima (primi 10):</p>
                            <div className="max-h-32 overflow-y-auto space-y-1">
                              {cleanupResult.sample.map((item, i) => (
                                <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-slate-700/50 rounded text-xs">
                                  <span className="text-white font-mono">{Object.values(item)[0]}</span>
                                  <span className="text-slate-400">{Object.values(item)[1]}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="p-3 border-t border-slate-700 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowCleanupModal(false);
                    setCleanupResult(null);
                  }}
                  disabled={cleanupLoading}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded transition-colors text-sm"
                >
                  Chiudi
                </button>
                {cleanupResult && cleanupResult.logs_count && cleanupResult.logs_count > 0 && !cleanupResult.executed && (
                  <button
                    onClick={handleCleanupExecute}
                    disabled={cleanupLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded transition-colors text-sm"
                  >
                    {cleanupLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                    Elimina {cleanupResult.logs_count} log
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LogViewerModal;
